import { Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import './Forms.css';

interface Education {
  id: string;
  degree: string;
  institution: string;
  location: string;
  startDate: string;
  endDate: string;
  gpa?: string;
  honors?: string;
}

const EducationForm: React.FC = () => {
  const navigate = useNavigate();
  const { currentJourney, updateJourneyData } = useJourney();
  
  const [educationList, setEducationList] = useState<Education[]>([{
    id: crypto.randomUUID(),
    degree: '',
    institution: '',
    location: '',
    startDate: '',
    endDate: '',
    gpa: '',
    honors: '',
  }]);
  
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentJourney?.collectedData?.education && currentJourney.collectedData.education.length > 0) {
      setEducationList(currentJourney.collectedData.education.map((edu: any) => ({
        id: edu.id || crypto.randomUUID(),
        degree: edu.degree || '',
        institution: edu.institution || '',
        location: edu.location || '',
        startDate: edu.startDate || '',
        endDate: edu.endDate || '',
        gpa: edu.gpa || '',
        honors: edu.honors || '',
      })));
    }
  }, [currentJourney]);

  const handleChange = (id: string, field: keyof Education, value: string) => {
    setEducationList(prev => prev.map(edu =>
      edu.id === id ? { ...edu, [field]: value } : edu
    ));
  };

  const addEducation = () => {
    setEducationList(prev => [...prev, {
      id: crypto.randomUUID(),
      degree: '',
      institution: '',
      location: '',
      startDate: '',
      endDate: '',
      gpa: '',
      honors: '',
    }]);
  };

  const removeEducation = (id: string) => {
    if (educationList.length > 1) {
      setEducationList(prev => prev.filter(edu => edu.id !== id));
    }
  };

  const validateForm = (): boolean => {
    for (const edu of educationList) {
      if (!edu.degree.trim() || !edu.institution.trim() || !edu.startDate || !edu.endDate) {
        alert('Please fill in all required fields (Degree, Institution, Start Date, End Date)');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await updateJourneyData({
        education: educationList,
      });
      
      // Move to next stage (Employment)
      navigate('/journey/forms/employment');
    } catch (error) {
      console.error('Failed to save education:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="stage-indicator">
          <span className="stage-number">Stage 2 of 5</span>
          <div className="progress-bar-thin">
            <div className="progress-fill-thin" style={{ width: '40%' }} />
          </div>
        </div>
        <h1>Education</h1>
        <p className="form-subtitle">Tell us about your academic background</p>
      </div>

      <form onSubmit={handleSubmit} className="resume-form">
        <div className="repeatable-cards">
          {educationList.map((edu, index) => (
            <div key={edu.id} className="card">
              <div className="card-header">
                <h3 className="card-title">Education #{index + 1}</h3>
                {educationList.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEducation(edu.id)}
                    className="btn-icon btn-remove"
                    title="Remove"
                  >
                    🗑️
                  </button>
                )}
              </div>

              <div className="form-row two-columns">
                <div className="form-field">
                  <label>Degree *</label>
                  <input
                    type="text"
                    value={edu.degree}
                    onChange={(e) => handleChange(edu.id, 'degree', e.target.value)}
                    placeholder="Bachelor of Science in Computer Science"
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Institution *</label>
                  <input
                    type="text"
                    value={edu.institution}
                    onChange={(e) => handleChange(edu.id, 'institution', e.target.value)}
                    placeholder="Stanford University"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Location</label>
                  <input
                    type="text"
                    value={edu.location}
                    onChange={(e) => handleChange(edu.id, 'location', e.target.value)}
                    placeholder="Stanford, CA"
                  />
                </div>
              </div>

              <div className="form-row two-columns">
                <div className="form-field">
                  <label>Start Date *</label>
                  <input
                    type="month"
                    value={edu.startDate}
                    onChange={(e) => handleChange(edu.id, 'startDate', e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>End Date *</label>
                  <input
                    type="month"
                    value={edu.endDate}
                    onChange={(e) => handleChange(edu.id, 'endDate', e.target.value)}
                    required
                  />
                  <small className="field-hint">Or expected graduation date</small>
                </div>
              </div>

              <div className="form-row two-columns">
                <div className="form-field">
                  <label>GPA <span className="optional">(Optional)</span></label>
                  <input
                    type="text"
                    value={edu.gpa}
                    onChange={(e) => handleChange(edu.id, 'gpa', e.target.value)}
                    placeholder="3.8/4.0"
                  />
                </div>

                <div className="form-field">
                  <label>Honors/Awards <span className="optional">(Optional)</span></label>
                  <input
                    type="text"
                    value={edu.honors}
                    onChange={(e) => handleChange(edu.id, 'honors', e.target.value)}
                    placeholder="Summa Cum Laude, Dean's List"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addEducation}
          className="btn-add"
        >
          <Plus size={16} />
          Add Another Degree
        </button>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/journey/forms/personal')}
            className="btn-secondary"
          >
            ← Back to Personal Info
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : 'Continue to Employment →'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EducationForm;

